import { z } from "zod";
import { ZodStringNullable } from "../zod-types/zod-string-nullable";

/**
 * What raising a return order asks for: the site the material is coming off, and optionally the
 * trucks fetching it.
 *
 * There is no article list. A return order is raised before anybody knows what will actually come
 * back — a truck is loaded with whatever is standing on the site — so the lines are the bookings
 * made on arrival, not a declaration made in advance.
 *
 * `vehicleIds` is a list because a site being cleared takes more than one lorry, and it is
 * optional because the office may raise the order before deciding which are going.
 */
export const returnOrderFormSchema = z.object({
	projectId: z.uuid(),
	note: ZodStringNullable,
	vehicleIds: z.array(z.uuid()).optional(),
});

export type ReturnOrderFormSchema = z.infer<typeof returnOrderFormSchema>;

/**
 * The trucks assigned to a return order, edited as a set.
 *
 * This is the whole of what a filed return order lets anybody change, and the narrowness is the
 * domain rather than an omission: there is no `PATCH /return-orders/:returnOrderId`, because the
 * site a run was raised for and the note it carries are what the printed sheet already says. The
 * fleet is the one part the office decides late and revises while a site is being cleared.
 *
 * **The whole set travels in one request**, to `PATCH /return-orders/:returnOrderId/vehicles`. The
 * per-truck `POST` and `DELETE` still stand for the caller changing one lorry, but every fleet
 * write reprints the run's sheet — one page per lorry — so reconciling a set through them renders
 * a PDF per truck touched and files four documents nobody wants beside the one they do.
 */
export const returnOrderVehiclesFormSchema = z.object({
	vehicleIds: z.array(z.uuid()),
});

export type ReturnOrderVehiclesFormSchema = z.infer<typeof returnOrderVehiclesFormSchema>;
